import { ApiClient } from '../api-client'
import type { ScheduledReportDTO, SchedulePeriodDTO, ScheduleDateRange, WhiteLabelConfigDTO } from 'api-models'

// Client for Scheduled Analytics Reports + white-label config (DEV-2377, §6). The
// wire shapes live in api-models (shared with the backend); re-exported here so
// existing `services/user` importers keep resolving them.
export type { ScheduledReportDTO, SchedulePeriodDTO, ScheduleDateRange, WhiteLabelConfigDTO } from 'api-models'

// UI cadence enum ↔ the row's `period` ({ value, unit }). The dropdown works in the
// enum; the wire uses period. This convention lives in exactly this one spot.
export type ScheduleInterval = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY'
export const intervalToPeriod = (interval: ScheduleInterval): SchedulePeriodDTO => {
	switch (interval) {
		case 'DAILY':
			return { value: 1, unit: 'day' }
		case 'MONTHLY':
			return { value: 1, unit: 'month' }
		case 'QUARTERLY':
			return { value: 3, unit: 'month' }
		case 'WEEKLY':
		default:
			return { value: 1, unit: 'week' }
	}
}
export const periodToInterval = (period: SchedulePeriodDTO | null | undefined): ScheduleInterval => {
	if (!period) return 'WEEKLY'
	if (period.unit === 'day') return 'DAILY'
	if (period.unit === 'week') return 'WEEKLY'
	return period.value === 3 ? 'QUARTERLY' : 'MONTHLY'
}

// Create/update request body — dates go over the wire as ISO strings (the DTO
// response types them as Date, per the api-models convention).
export interface ScheduledReportPayload {
	label: string
	/** Required — schedules are view-based; generation reads the live view config. */
	view_id: string
	ad_account_ids?: string[]
	campaign_ids?: string[]
	period: SchedulePeriodDTO
	analytics_date_range?: ScheduleDateRange
	starting_date: string
	recipients: string[]
}

export interface ScheduledReportStats {
	all: number
	dueThisWeek: number
	byMe: number
}

const base = (accountId: string, assetId: string) =>
	`/accounts/${accountId}/assets/${assetId}/scheduled-reports`

const list = async (accountId: string, assetId: string): Promise<ScheduledReportDTO[]> => {
	const { data } = await ApiClient.client.get(base(accountId, assetId))
	return data.data
}

const stats = async (accountId: string, assetId: string): Promise<ScheduledReportStats> => {
	const { data } = await ApiClient.client.get(`${base(accountId, assetId)}/stats`)
	return data.data
}

const create = async (
	accountId: string,
	assetId: string,
	payload: ScheduledReportPayload
): Promise<ScheduledReportDTO> => {
	const { data } = await ApiClient.client.post(base(accountId, assetId), payload)
	return data.data
}

const update = async (
	accountId: string,
	assetId: string,
	id: string,
	patch: Partial<ScheduledReportPayload>
): Promise<ScheduledReportDTO> => {
	const { data } = await ApiClient.client.patch(`${base(accountId, assetId)}/${id}`, patch)
	return data.data
}

const stop = async (accountId: string, assetId: string, id: string): Promise<void> => {
	await ApiClient.client.delete(`${base(accountId, assetId)}/${id}`)
}

const downloadLast = async (
	accountId: string,
	assetId: string,
	id: string
): Promise<{ url: string }> => {
	const { data } = await ApiClient.client.get(`${base(accountId, assetId)}/${id}/download`)
	return data.data
}

// White-label config lives on the asset (website-level).
const saveWhiteLabel = async (
	accountId: string,
	assetId: string,
	config: {
		enabled: boolean
		logoSource: 'workspace' | 'website' | 'upload' | null
		brandColor: string | null
		uploadedLogoUrl?: string | null
	}
): Promise<WhiteLabelConfigDTO> => {
	const { data } = await ApiClient.client.patch(
		`/accounts/${accountId}/assets/${assetId}/white-label`,
		config
	)
	return data.data
}

const uploadWhiteLabelLogo = async (
	accountId: string,
	assetId: string,
	file: File
): Promise<string> => {
	const form = new FormData()
	form.append('logo', file)
	// Commit the URL together with the profile only after the subsequent PATCH.
	form.append('deferCommit', 'true')
	const { data } = await ApiClient.client.post(
		`/accounts/${accountId}/assets/${assetId}/white-label/logo`,
		form,
		{ headers: { 'Content-Type': 'multipart/form-data' } }
	)
	return data.data
}

export type WhiteLabelLogoImageSource = 'workspace' | 'website' | 'upload'

// The header logo for a white-label source, as bytes streamed through the API.
// Avatars and uploads live in a storage bucket that serves no CORS headers: the
// browser can render them in an <img> but can't fetch() them to bake into the
// downloaded PDF, so the API proxies the file (assets-api /white-label/logo-image).
const whiteLabelLogoBlob = async (
	accountId: string,
	assetId: string,
	source: WhiteLabelLogoImageSource
): Promise<Blob> => {
	const { data } = await ApiClient.client.get(
		`/accounts/${accountId}/assets/${assetId}/white-label/logo-image`,
		// A replaced logo must not reuse the proxy's previously cached response.
		{ params: { source, v: Date.now() }, responseType: 'blob' }
	)
	return data
}

export const ScheduledReports = {
	list,
	stats,
	create,
	update,
	stop,
	downloadLast,
	saveWhiteLabel,
	uploadWhiteLabelLogo,
	whiteLabelLogoBlob
}
